import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { HistoryIcon, SendIcon, UndoIcon } from 'lucide-react';
import { PERMISSIONS } from '@siteflow/shared';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { EntryStatusChip } from '@/components/content/entry-status-chip';
import { VersionHistoryDrawer } from '@/components/content/version-history-drawer';
import { EmptyState } from '@/components/empty-state';
import { PermissionGate } from '@/components/permission-gate';
import { EnvironmentSelect } from '@/components/sites/environment-select';
import { useSiteScope } from '@/components/sites/site-scope';
import { CONTENT_ONLY } from '@/lib/ui-mode';
import { plainFieldLabel, plainSectionTitle } from '@/lib/content/field-meta';
import { pagesQuery } from '@/lib/api/pages';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { messageForError } from '@/lib/api-client';
import {
  contentApi,
  contentKeys,
  entriesQuery,
  publishEventsQuery,
} from '@/lib/api/content';
import { membersQuery } from '@/lib/api/members';
import { siteKeys } from '@/lib/api/sites';
import type { ContentEntry } from '@/lib/api/types';
import { useOrgPermissions } from '@/lib/permissions';
import { formatDateTime } from '@/lib/utils';

const ACTION_LABEL = {
  published: 'Published',
  unpublished: 'Unpublished',
  restored: 'Restored',
} as const;

function PublishPanel() {
  const { orgId, siteId } = Route.useParams();
  const queryClient = useQueryClient();
  const { environments, environment, environmentId, setEnvironmentId } = useSiteScope();
  const { has } = useOrgPermissions(orgId);
  const canPublish = has(PERMISSIONS.CONTENT_PUBLISH);
  const canViewHistory = has(PERMISSIONS.CONTENT_VERSIONS_VIEW);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmAll, setConfirmAll] = useState(false);
  const [unpublishTarget, setUnpublishTarget] = useState<ContentEntry[] | null>(null);
  const [historyEntry, setHistoryEntry] = useState<ContentEntry | null>(null);

  const entries = useQuery(entriesQuery(orgId, siteId, { environmentId }));
  const events = useQuery(publishEventsQuery(orgId, siteId, 25));
  const members = useQuery(membersQuery(orgId));
  const pages = useQuery({ ...pagesQuery(orgId, siteId), enabled: CONTENT_ONLY });
  const pageNames = useMemo(
    () => new Map((pages.data ?? []).map((page) => [page.slug, page.name])),
    [pages.data],
  );

  const pending = useMemo(
    () =>
      (entries.data ?? []).filter(
        (entry) => entry.status === 'draft' || entry.status === 'changed',
      ),
    [entries.data],
  );

  const live = useMemo(
    () => (entries.data ?? []).filter((entry) => entry.status === 'published'),
    [entries.data],
  );

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: contentKeys.all(orgId, siteId) });
    void queryClient.invalidateQueries({ queryKey: siteKeys.all(orgId) });
  }

  const publishMutation = useMutation({
    mutationFn: (entryIds?: string[]) =>
      contentApi.publish(orgId, siteId, {
        environmentId: environmentId!,
        ...(entryIds ? { entryIds } : {}),
      }),
    onSuccess: (result) => {
      toast.success(
        result.publishedCount === 0
          ? 'Nothing needed publishing'
          : CONTENT_ONLY
            ? `${result.publishedCount} ${result.publishedCount === 1 ? 'change is' : 'changes are'} now live`
            : `${result.publishedCount} ${result.publishedCount === 1 ? 'entry' : 'entries'} published — revision ${result.revision}`,
      );
      setSelected(new Set());
      setConfirmAll(false);
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'Publishing failed.')),
  });

  const unpublishMutation = useMutation({
    mutationFn: (entryIds: string[]) =>
      contentApi.unpublish(orgId, siteId, { environmentId: environmentId!, entryIds }),
    onSuccess: (result) => {
      toast.success(
        `${result.unpublishedCount} ${result.unpublishedCount === 1 ? 'entry' : 'entries'} withdrawn`,
      );
      setUnpublishTarget(null);
      setSelected(new Set());
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'Unpublishing failed.')),
  });

  const authorName = (userId: string | null): string => {
    if (!userId) return 'System';
    return members.data?.find((member) => member.userId === userId)?.name ?? 'Unknown user';
  };

  const allSelected = pending.length > 0 && selected.size === pending.length;

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(pending.map((entry) => entry.id)));
  }

  function toggle(entryId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="max-w-prose">
          <h2 className="text-lede font-semibold tracking-tight text-ink">Publish</h2>
          <p className="text-data leading-relaxed text-ink-muted">
            {CONTENT_ONLY
              ? 'Your changes stay private until you publish them. Publishing puts everything that is waiting onto the live website.'
              : 'Drafts live only in the dashboard. Publishing copies the current draft into the chosen environment and bumps its revision, which is what the SDK on the live site checks.'}
          </p>
        </div>
        {CONTENT_ONLY ? null : (
          <EnvironmentSelect
            environments={environments}
            value={environmentId}
            onChange={setEnvironmentId}
            label="Publish to"
          />
        )}
      </div>

      {environment ? (
        <p className="text-micro text-ink-subtle" data-numeric>
          {CONTENT_ONLY
            ? 'Last published '
            : `${environment.name} is on revision ${environment.contentRevision} · last published `}
          {environment.lastPublishedAt ? formatDateTime(environment.lastPublishedAt) : 'never'}
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3">
            <div>
              <span className="section-label">Waiting</span>
              <CardTitle>
                {pending.length} {pending.length === 1 ? 'entry' : 'entries'} not yet live
              </CardTitle>
            </div>
            <PermissionGate
              permission={PERMISSIONS.CONTENT_PUBLISH}
              fallback={
                <Badge
                  variant="neutral"
                  title={
                    CONTENT_ONLY
                      ? 'You can edit but not publish. Ask a publisher to make your changes live.'
                      : 'Publishing needs the content.publish permission'
                  }
                >
                  Read only
                </Badge>
              }
            >
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={selected.size === 0 || publishMutation.isPending || !environmentId}
                  onClick={() => publishMutation.mutate([...selected])}
                >
                  <SendIcon />
                  Publish selected ({selected.size})
                </Button>
                <Button
                  size="sm"
                  disabled={pending.length === 0 || publishMutation.isPending || !environmentId}
                  onClick={() => setConfirmAll(true)}
                >
                  Publish all
                </Button>
              </div>
            </PermissionGate>
          </CardHeader>

          <CardContent className="flex flex-col gap-3">
            {entries.isLoading ? (
              <div className="flex flex-col gap-2" aria-busy="true">
                {Array.from({ length: 4 }, (_, index) => (
                  <Skeleton key={index} className="h-9 w-full" />
                ))}
              </div>
            ) : null}

            {entries.isError ? (
              <p role="alert" className="text-data text-danger">
                {messageForError(entries.error, 'The entry list could not be loaded.')}
              </p>
            ) : null}

            {!entries.isLoading && pending.length === 0 && !entries.isError ? (
              <EmptyState
                icon={SendIcon}
                title="Nothing waiting"
                description={`Every entry on this site matches what ${environment?.name ?? 'this environment'} is serving. Edit some content and it will appear here as a draft or a changed entry.`}
              />
            ) : null}

            {pending.length > 0 ? (
              <>
                <label className="flex items-center gap-2 border-b border-hairline pb-2 text-data text-ink">
                  <Checkbox
                    checked={allSelected ? true : selected.size > 0 ? 'indeterminate' : false}
                    onCheckedChange={toggleAll}
                    aria-label="Select every waiting entry"
                    disabled={!canPublish}
                  />
                  Select all
                </label>

                <ul className="flex flex-col">
                  {pending.map((entry) => (
                    <li
                      key={entry.id}
                      className="flex flex-wrap items-center gap-2 border-b border-hairline py-2 last:border-b-0"
                    >
                      <Checkbox
                        checked={selected.has(entry.id)}
                        onCheckedChange={() => toggle(entry.id)}
                        disabled={!canPublish}
                        aria-label={`Select ${entry.key}`}
                      />
                      <div className="min-w-0 flex-1">
                        {CONTENT_ONLY ? (
                          <span className="block truncate text-data font-medium text-ink">
                            {plainSectionTitle(entry.key)} · {plainFieldLabel(entry.key)}
                          </span>
                        ) : (
                          <code className="block truncate font-mono text-data text-ink">
                            {entry.key}
                          </code>
                        )}
                        <span className="text-micro text-ink-subtle">
                          {CONTENT_ONLY
                            ? `${entry.pageSlug ? (pageNames.get(entry.pageSlug) ?? 'A page') : 'Every page'} · `
                            : `${entry.pageSlug ?? 'Site-wide'} · ${entry.type} · `}
                          updated <span data-numeric>{formatDateTime(entry.updatedAt)}</span>
                        </span>
                      </div>
                      <EntryStatusChip status={entry.status} />
                      {canViewHistory ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          aria-label={`Version history for ${entry.key}`}
                          onClick={() => setHistoryEntry(entry)}
                        >
                          <HistoryIcon className="size-3.5" />
                          History
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {live.length > 0 ? (
              <PermissionGate permission={PERMISSIONS.CONTENT_PUBLISH}>
                <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-3">
                  <span className="section-label flex-1">
                    {live.length} live {live.length === 1 ? 'entry' : 'entries'}
                  </span>
                  <Button
                    variant="outlineDanger"
                    size="sm"
                    disabled={selected.size === 0}
                    onClick={() =>
                      setUnpublishTarget(
                        (entries.data ?? []).filter((entry) => selected.has(entry.id)),
                      )
                    }
                  >
                    <UndoIcon />
                    Unpublish selected
                  </Button>
                </div>
              </PermissionGate>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <span className="section-label">Activity</span>
            <CardTitle>Recent publishes</CardTitle>
          </CardHeader>
          <CardContent>
            {events.isLoading ? (
              <div className="flex flex-col gap-2" aria-busy="true">
                {Array.from({ length: 5 }, (_, index) => (
                  <Skeleton key={index} className="h-8 w-full" />
                ))}
              </div>
            ) : (events.data?.length ?? 0) === 0 ? (
              <p className="text-data text-ink-muted">
                No publish events recorded for this site yet.
              </p>
            ) : (
              <ol className="flex flex-col">
                {(events.data ?? []).map((event) => {
                  const target = environments.find(
                    (item) => item.id === event.siteEnvironmentId,
                  );
                  return (
                    <li
                      key={event.id}
                      className="flex flex-wrap items-center gap-2 border-b border-hairline py-2 last:border-b-0"
                    >
                      <Badge
                        variant={
                          event.action === 'published'
                            ? 'success'
                            : event.action === 'unpublished'
                              ? 'danger'
                              : 'neutral'
                        }
                      >
                        {ACTION_LABEL[event.action]}
                      </Badge>
                      <span className="min-w-0 flex-1 truncate text-micro text-ink-muted">
                        {authorName(event.actorUserId)}
                        {CONTENT_ONLY ? '' : ` · ${target?.name ?? 'Unknown environment'}`}
                      </span>
                      <span className="text-micro text-ink-subtle" data-numeric>
                        {formatDateTime(event.createdAt)}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmAll}
        onOpenChange={setConfirmAll}
        title={
          CONTENT_ONLY
            ? `Publish ${pending.length === 1 ? 'this change' : `all ${pending.length} changes`}?`
            : `Publish everything to ${environment?.name ?? 'this environment'}?`
        }
        confirmLabel={`Publish ${pending.length}`}
        pending={publishMutation.isPending}
        description={
          CONTENT_ONLY ? (
            <>
              {pending.length === 1 ? 'This change goes' : `All ${pending.length} changes go`} on the
              website now. Visitors see the new text the next time they open the page. Earlier
              versions are kept, so you can always go back.
            </>
          ) : (
            <>
              All {pending.length} waiting {pending.length === 1 ? 'entry' : 'entries'} become live
              on <strong className="text-ink">{environment?.name}</strong> immediately. Visitors see
              the new copy on their next page load. Every previous version stays in history, so
              this can be reversed.
            </>
          )
        }
        onConfirm={() => publishMutation.mutate(undefined)}
      />

      <ConfirmDialog
        open={unpublishTarget !== null}
        onOpenChange={(open) => !open && setUnpublishTarget(null)}
        destructive
        title="Withdraw from the live site?"
        confirmLabel={`Unpublish ${unpublishTarget?.length ?? 0}`}
        pending={unpublishMutation.isPending}
        description={
          CONTENT_ONLY ? (
            <>
              The website goes back to its original text for{' '}
              {(unpublishTarget?.length ?? 0) === 1 ? 'this item' : 'these items'}. Your changes
              stay here as drafts and can be published again.
            </>
          ) : (
            <>
              {unpublishTarget?.length ?? 0}{' '}
              {(unpublishTarget?.length ?? 0) === 1 ? 'entry stops' : 'entries stop'} being served
              to <strong className="text-ink">{environment?.name}</strong>. The elements on the page
              fall back to whatever is hard-coded in the markup. The drafts stay here and can be
              published again.
            </>
          )
        }
        onConfirm={() =>
          unpublishTarget && unpublishMutation.mutate(unpublishTarget.map((entry) => entry.id))
        }
      />

      <VersionHistoryDrawer
        open={historyEntry !== null}
        onOpenChange={(open) => !open && setHistoryEntry(null)}
        orgId={orgId}
        siteId={siteId}
        entry={historyEntry}
      />
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/$siteId/publish')({
  component: PublishPanel,
});
